import React from 'react';
import './HelpBadge.css';

type HelpBadgeProps = {
  label: string;
  children: React.ReactNode;
  id?: string;
  wide?: boolean;
};

/**
 * Accessible contextual help control ("?").
 * Opens on click; closes on Escape / outside click / blur leaving the control.
 * Kept out of Tab order so form fields tab field-to-field; still clickable with the mouse.
 * Prefer short help (1–3 sentences). Use page-level docs for long procedures.
 */
const HelpBadge: React.FC<HelpBadgeProps> = ({ label, children, id, wide = false }) => {
  const panelId = React.useId();
  const resolvedId = id || panelId;
  const rootRef = React.useRef<HTMLSpanElement | null>(null);
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      return undefined;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };
    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (target && rootRef.current && !rootRef.current.contains(target)) {
        setOpen(false);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [open]);

  return (
    <span className="help-badge" ref={rootRef}>
      <button
        type="button"
        className={`help-badge__trigger${open ? ' is-open' : ''}`}
        aria-label={label}
        aria-expanded={open}
        aria-controls={resolvedId}
        tabIndex={-1}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setOpen((current) => !current);
        }}
      >
        <span aria-hidden="true">?</span>
      </button>
      {open ? (
        <span
          id={resolvedId}
          className={`help-badge__panel${wide ? ' help-badge__panel--wide' : ''}`}
          role="note"
        >
          {children}
        </span>
      ) : null}
    </span>
  );
};

export default HelpBadge;
